import * as React from 'react';
import { useContext } from 'react';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import type { EnterpriseNoticeAcceptanceLogsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseNoticeAcceptanceLogsV1';
import type { ProgramMembershipEdge } from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';
import type { RecoveryEmailData } from 'bundles/unified-common/hooks/useRecoveryEmail';

export type EnterpriseOrgUserExperienceContextType = {
  programMembershipsAndInvitations?: ProgramMembershipEdge[];
  isProgramMembershipsAndInvitationsLoading?: boolean;
  isMembershipsLoadError?: boolean;
  acceptanceNoticeLogs?: EnterpriseNoticeAcceptanceLogsV1[];
  isAcceptanceNoticeLogsLoading?: boolean;
  refetchAcceptanceNoticeLogs?: () => Promise<unknown> | unknown;
  requirePreJoinCheck?: boolean;
  userEmails?: UserEmail[];
  isUserEmailsLoading?: boolean;
  refetchUserEmails?: () => Promise<unknown> | unknown;
  recoveryEmail?: RecoveryEmailData | null;
  isRecoveryEmailLoading?: boolean;
};

const EnterpriseOrgUserExperienceContext = React.createContext<EnterpriseOrgUserExperienceContextType | undefined>(
  undefined
);

/**
 * @description Hook to get user program memberships and invitations (org-scoped only)
 */
export const useProgramMembershipsAndInvitations = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return context?.programMembershipsAndInvitations;
};

/**
 * @description Hook to get the program memberships and invitations loading state (org-scoped only)
 */
export const useIsProgramMembershipsAndInvitationsLoading = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return context?.isProgramMembershipsAndInvitationsLoading;
};

/**
 * @description Hook to get acceptance notice logs for privacy notice compliance (org-scoped only)
 */
export const useAcceptanceNoticeLogs = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return context?.acceptanceNoticeLogs;
};

/**
 * @description Hook to get the acceptance notice logs loading state (org-scoped only)
 */
export const useIsAcceptanceNoticeLogsLoading = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return context?.isAcceptanceNoticeLogsLoading;
};

/**
 * @description Hook to check if pre-join privacy notice check is required (org-scoped only)
 */
export const useRequirePreJoinCheck = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return context?.requirePreJoinCheck;
};

export const useRefetchAcceptanceNoticeLogs = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return context?.refetchAcceptanceNoticeLogs;
};

/**
 * @description Hook to get user emails for recovery email modal (org-scoped only)
 */
export const useOrgScopedUserEmails = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return {
    userEmails: context?.userEmails,
    isUserEmailsLoading: context?.isUserEmailsLoading,
    refetchUserEmails: context?.refetchUserEmails,
  };
};

/**
 * @description Hook to get recovery email data (org-scoped only)
 */
export const useOrgScopedRecoveryEmail = () => {
  const context = useContext(EnterpriseOrgUserExperienceContext);

  return {
    recoveryEmail: context?.recoveryEmail,
    isRecoveryEmailLoading: context?.isRecoveryEmailLoading,
  };
};

export default EnterpriseOrgUserExperienceContext;
